import { useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Play, Pause, SpeakerHigh, ArrowCounterClockwise, Spinner } from "@phosphor-icons/react";

const fmt = (s) => { if (!isFinite(s)) return "0:00"; const m = Math.floor(s / 60); const r = Math.floor(s % 60); return `${m}:${r < 10 ? "0" : ""}${r}`; };

/** Play/pause + progress for a toolbox talk's spoken audio. `base` = "/hs/toolbox-talks" (staff) or "/in-house-portal/toolbox-talks" (operative).
 *  Fetches the MP3 through the authenticated API on first play, then plays from a blob URL. Polls while the audio is still being prepared. */
export default function TalkAudioPlayer({ talkId, audio, base = "/in-house-portal/toolbox-talks", onRefresh, compact = false, canRegenerate = false, testId = "talk-audio" }) {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused | error
  const [err, setErr] = useState("");
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [regen, setRegen] = useState(false);
  const ref = useRef(null);
  const urlRef = useRef("");
  const status = audio?.status;

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);
  useEffect(() => {
    if (status !== "generating" || !onRefresh) return undefined;
    const id = setInterval(onRefresh, 6000);
    return () => clearInterval(id);
  }, [status, onRefresh]);

  const ensure = async () => {
    if (urlRef.current) return urlRef.current;
    const { data } = await API.get(`${base}/${talkId}/audio`, { responseType: "blob" });
    urlRef.current = URL.createObjectURL(data);
    return urlRef.current;
  };

  const toggle = async () => {
    try {
      if (state === "playing") { ref.current?.pause(); setState("paused"); return; }
      setState("loading"); setErr("");
      const url = await ensure();
      if (!ref.current) {
        const el = new Audio(url);
        el.addEventListener("timeupdate", () => setT(el.currentTime));
        el.addEventListener("loadedmetadata", () => setDur(el.duration));
        el.addEventListener("ended", () => { setState("paused"); setT(0); el.currentTime = 0; });
        el.addEventListener("error", () => { setState("error"); setErr("Playback failed"); });
        ref.current = el;
      }
      await ref.current.play();
      setState("playing");
    } catch (e) { setState("error"); setErr(formatApiError(e)); }
  };

  const seek = (e) => {
    if (!ref.current || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    ref.current.currentTime = pct * dur; setT(pct * dur);
  };

  const regenerate = async () => {
    setRegen(true);
    try { await API.post(`${base}/${talkId}/audio/regenerate`); urlRef.current = ""; ref.current = null; setT(0); setDur(0); setState("idle"); onRefresh?.(); } catch (e) { setErr(formatApiError(e)); } finally { setRegen(false); }
  };

  if (!status || status === "generating") {
    return <p className={`text-[11px] text-muted-foreground inline-flex items-center gap-1.5 ${compact ? "" : "mt-2"}`} data-testid={`${testId}-preparing`}><Spinner size={12} className="animate-spin" /> {status === "generating" ? "Audio version being prepared…" : "No audio yet"}{canRegenerate && status !== "generating" && <button type="button" onClick={regenerate} disabled={regen} className="text-primary hover:underline ml-1" data-testid={`${testId}-generate`}>Generate</button>}</p>;
  }
  if (status === "failed") {
    return <p className={`text-[11px] text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5 ${compact ? "" : "mt-2"}`} data-testid={`${testId}-failed`}><SpeakerHigh size={12} /> Audio unavailable{audio?.error ? ` (${audio.error})` : ""}{canRegenerate && <button type="button" onClick={regenerate} disabled={regen} className="text-primary hover:underline ml-1" data-testid={`${testId}-retry`}>{regen ? "Retrying…" : "Try again"}</button>}</p>;
  }
  const total = dur || audio?.duration_est_s || 0;
  return (
    <div className={`flex items-center gap-2 ${compact ? "" : "mt-2"} rounded-md border border-border bg-muted/30 px-2 py-1.5`} data-testid={`${testId}-player`} onClick={e => e.stopPropagation()}>
      <Button size="sm" variant={state === "playing" ? "default" : "outline"} className="h-8 w-8 p-0 rounded-full shrink-0" onClick={toggle} disabled={state === "loading"} aria-label={state === "playing" ? "Pause talk" : "Play talk"} data-testid={`${testId}-toggle`}>
        {state === "loading" ? <Spinner size={14} className="animate-spin" /> : state === "playing" ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
      </Button>
      <div className="flex-1 min-w-0">
        <div className="h-1.5 rounded-full bg-border cursor-pointer overflow-hidden" onClick={seek} data-testid={`${testId}-progress`}>
          <div className="h-full bg-primary transition-[width] duration-200" style={{ width: total ? `${Math.min(100, (t / total) * 100)}%` : "0%" }} />
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1"><SpeakerHigh size={10} /> {state === "idle" ? "Listen to this talk" : `${fmt(t)} / ${fmt(total)}`}{err ? ` · ${err}` : ""}</p>
      </div>
      {canRegenerate && <button type="button" onClick={regenerate} disabled={regen} title="Re-generate the audio" className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`${testId}-regenerate`}><ArrowCounterClockwise size={13} className={regen ? "animate-spin" : ""} /></button>}
    </div>
  );
}
